'use client';

import { createPortal } from 'react-dom';
import CreativeEditorSDK, { AssetResult } from '@cesdk/cesdk-js';
import { reportErrorToFaro, fetchWithHeaders } from '@sendible/common';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { Spinner, useToastNotificationContext } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { EVENTS, subscribe } from '@sendible/event-manager';
import CreativeEditor from '@cesdk/cesdk-js/react';
import {
  BlurAssetSource,
  CaptionPresetsAssetSource,
  ImageColorsAssetSource,
  ColorPaletteAssetSource,
  CropPresetsAssetSource,
  EffectsAssetSource,
  FiltersAssetSource,
  StickerAssetSource,
  TextComponentAssetSource,
  TypefaceAssetSource,
  TextAssetSource,
  VectorShapeAssetSource,
  UploadAssetSources,
} from '@cesdk/cesdk-js/plugins';
import React, { useCallback, useRef, useState } from 'react';
import { CeSDKWrapper, LoadingWrapper } from './index.styles';

import PAGE_FORMAT_ASSETS from './VideoEditor/PageFormatAssets.json';
import { VideoEditorConfig } from './VideoEditor/plugin';
import { useUploadsContext } from '../../models/uploads/uploadsContext';
import { getUrlWithCredentialsFromUser } from '../../utils';
import { useQueryMediaEndpontForSourceSets } from '../../models/medias';
import { EDITOR_CLOSE, EDITOR_CLOSE_VIDEO, EDITOR_VIDEO_EXPORT } from '.';

const MemoCreativeEditor = React.memo(CreativeEditor);

type SourceSetsType = { url: string; height: string; width: string }[];

const Editor = ({
  domainUrl,
  user,
  originalMediaId,
  originalUrl,
  resetState,
  fileName,
  serviceSet,
  restrictedServices,
  metadata,
}: OpenVideoEditorDataType) => {
  const { showToastNotification } = useToastNotificationContext();
  const { t } = useTranslation('media_library');
  const { uploadFiles } = useUploadsContext();
  const [isEditorReady, setIsEditorReady] = useState(false);

  const { data: media, isSuccess } = useQueryMediaEndpontForSourceSets(originalMediaId, user);

  const mediaRef = useRef({ media, isSuccess });

  React.useEffect(() => {
    mediaRef.current = { media, isSuccess };
  }, [media, isSuccess]);

  const matchedServiceAssets = PAGE_FORMAT_ASSETS.assets.filter((asset) => !!asset.meta.service.includes(serviceSet));
  const filteredCropAssets = matchedServiceAssets.length === 0 ? PAGE_FORMAT_ASSETS.assets : matchedServiceAssets;
  const filtedPageFormatAssets = { ...PAGE_FORMAT_ASSETS, assets: filteredCropAssets };

  if (restrictedServices.pinterest) {
    filtedPageFormatAssets.assets = filtedPageFormatAssets.assets.filter((item) => item.meta.service !== 'pinterest');
  }

  const saveVideo = async (cesdk: CreativeEditorSDK) => {
    const abortSignal = new AbortController();
    const dialog = cesdk.ui.showDialog({
      type: 'loading',
      actions: {
        label: 'Cancel',
        onClick: (context) => {
          cesdk.ui.closeDialog(context.id);
          abortSignal.abort();
        },
      },
      clickOutsideToClose: false,
      content: {
        title: 'Processing (step 1 of 2)',
        message: 'Do not close this window.',
      },
      progress: 0,
    });

    const page = cesdk.engine.scene.getCurrentPage();

    try {
      const blob = await cesdk.engine.block.exportVideo(
        page as number,
        'video/mp4',
        (numberOfRenderedFrames, numberOfEncodedFrames, totalNumberOfFrames) => {
          const progress = Math.round((numberOfRenderedFrames / totalNumberOfFrames) * 100);

          cesdk.ui.updateDialog(dialog, { progress });
        },
        {
          abortSignal: abortSignal.signal,
          videoBitrate: metadata.videoBitsPerSecond,
          framerate: metadata.framesPerSecond,
        }
      );

      if (blob) {
        const file = new File([blob], fileName, {
          type: 'video/mp4',
          lastModified: Date.now(),
        });

        await fetchWithHeaders({
          method: 'POST',
          url: getUrlWithCredentialsFromUser(`https://${WEBPACK_API}/api/v2/error_log.json`, user),
          params: {
            message: `[CE.SDK] Video Editor Export from domain: ${domainUrl}`,
            stacktrace: 'n/a',
            source: 'web',
            type: 'usage-tracking',
          },
        });

        cesdk.ui.updateDialog(dialog, {
          content: { title: 'Uploading (step 2 of 2)', message: 'Do not close this window.' },
          progress: 0,
        });

        uploadFiles({
          files: [file],
          onUploadStarted: () => Math.random().toString(),
          onMediaCreated: (mediaCreatedObj) => {
            const unsubscribe = subscribe(EVENTS.UPLOAD_READY, (mediaObj: MediaType) => {
              if (mediaCreatedObj.mediaId === mediaObj.id) {
                window.pendo.track('[CE.SDK] Editor Export', { fileType: 'video' });
                window.parent.postMessage({ event: EDITOR_VIDEO_EXPORT, originalMediaId, media: mediaObj }, '*');
                window.parent.postMessage({ event: EDITOR_CLOSE_VIDEO }, '*');
                resetState();
                unsubscribe();
              }
            });
          },
        });

        subscribe(EVENTS.UPLOAD_PROGRESS, ({ uploadProgress }) => {
          cesdk.ui.updateDialog(dialog, { progress: uploadProgress });
        });
      } else {
        throw new Error('Video Blob not generated');
      }
    } catch (error: unknown) {
      if (error instanceof Error && error?.message === 'AbortSignal received') {
        return;
      }

      reportErrorToFaro(error, '(CE.SDK) Editor Export Failure');

      cesdk.ui.closeDialog(dialog);
      showToastNotification({
        notificationText: t('video_not_saved') as string,
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
    }
  };

  const resizeScene = (engine: CreativeEditorSDK['engine'], width: number, height: number) => {
    if (width <= 0 || height <= 0) return;

    const scene = engine.scene.get();
    const pageIds = engine.block.findByType('page');

    engine.block.resizeContentAware(pageIds, width, height);

    if (scene != null) {
      engine.block.setFloat(scene, 'scene/pageDimensions/width', width);
      engine.block.setFloat(scene, 'scene/pageDimensions/height', height);
    }
  };

  const setupCropPanel = (cesdk: CreativeEditorSDK, originalSceneSize: { width: number; height: number } | null) => {
    // ========================================================================
    // Scene format presets (from your pageFormats manifest)
    // ========================================================================

    const BASE_URL = '/video'; // keep whatever path made your thumbnails load
    const FORMATS_SOURCE = 'ly.img.scene.formats';
    const FORMATS_ENTRY = 'ly.img.scene.formats.entry';
    const TRANSFORM_PANEL = 'ly.img.crop.transform';
    const BASE_SIZE = 1080; // shorter edge of the output canvas

    const dimsFromAspect = (aspectRatio: string, base = BASE_SIZE) => {
      const [rw, rh] = aspectRatio.split(':').map(Number);

      return rw >= rh ? { width: Math.round(base * (rw / rh)), height: base } : { width: base, height: Math.round(base * (rh / rw)) };
    };

    const transformPageFormats = (manifest: typeof filtedPageFormatAssets, baseUrl: string) => {
      return manifest.assets.map((a) => {
        const { width, height } = dimsFromAspect(a.meta.aspectRatio);
        const thumb = a.meta.thumbUri.replace('{{base_url}}', baseUrl);

        return {
          id: a.id,
          label: a.label,
          meta: {
            width,
            height,
            aspectRatio: a.meta.aspectRatio,
            thumbUri: thumb,
            uri: thumb, // harmless (applyAsset is overridden); gives the grid something to paint
          },
        };
      });
    };

    // Register the source — applyAsset (tile click) resizes the whole scene
    cesdk.engine.asset.addLocalSource(FORMATS_SOURCE, undefined, async (asset) => {
      let w = Number(asset.meta?.width);
      let h = Number(asset.meta?.height);

      if ((!w || !h) && asset.meta?.aspectRatio) {
        ({ width: w, height: h } = dimsFromAspect(asset.meta.aspectRatio as string));
      }
      if (w && h) resizeScene(cesdk.engine, w, h);

      return undefined;
    });

    transformPageFormats(filtedPageFormatAssets, BASE_URL).forEach((asset) => cesdk.engine.asset.addAssetToSource(FORMATS_SOURCE, asset));

    // ========================================================================
    // Custom Crop panel: Dimensions + Lock + Apply + Reset + preset grid
    // ========================================================================

    // Panel header text (otherwise shows the raw panel id)
    cesdk.i18n.setTranslations({ en: { 'panel.ly.img.crop.transform': 'Crop' } });

    cesdk.ui.registerPanel(TRANSFORM_PANEL, ({ builder, engine, state }) => {
      const width = state('width', originalSceneSize?.width ?? 1920);
      const height = state('height', originalSceneSize?.height ?? 1080);
      const lock = state('lock', true);

      builder.Section('crop.dimensions', {
        title: 'Dimensions',
        children: () => {
          builder.NumberInput('width', {
            inputLabel: 'Width (px)',
            value: width.value,
            min: 1,
            setValue: (v: number) => {
              if (lock.value && width.value) height.setValue(Math.round(v * (height.value / width.value)));
              width.setValue(v);
            },
          });
          builder.NumberInput('height', {
            inputLabel: 'Height (px)',
            value: height.value,
            min: 1,
            setValue: (v: number) => {
              if (lock.value && height.value) width.setValue(Math.round(v * (width.value / height.value)));
              height.setValue(v);
            },
          });
          builder.Checkbox('lock', {
            inputLabel: 'Lock Aspect Ratio',
            inputLabelPosition: 'right',
            value: lock.value,
            setValue: lock.setValue,
          });

          builder.Button('apply', {
            label: 'Apply',
            color: 'accent',
            onClick: () => resizeScene(engine, Number(width.value), Number(height.value)),
          });
          builder.Button('reset', {
            label: 'Reset',
            variant: 'plain',
            onClick: () => {
              if (originalSceneSize) {
                width.setValue(originalSceneSize.width);
                height.setValue(originalSceneSize.height);
                resizeScene(engine, originalSceneSize.width, originalSceneSize.height);
              }
            },
          });
        },
      });

      builder.Section('crop.presets', {
        title: 'Presets',
        children: () => {
          // Inline entry (not a registered id) + cardLabelPosition 'below' is what
          // renders the label UNDER the thumbnail, like your videoTransformPanel.
          builder.Library('crop-presets', {
            entries: [
              {
                id: FORMATS_ENTRY,
                sourceIds: [FORMATS_SOURCE],
                previewLength: 3,
                gridColumns: 3,
                gridItemHeight: 'square',
                previewBackgroundType: 'contain',
                gridBackgroundType: 'cover',
                cardLabel: (assetResult: AssetResult) => assetResult.label,
                cardLabelPosition: () => 'below',
                showGroupOverview: false,
              },
            ],
          });
        },
      });
    });

    // Dock button that toggles the custom Crop panel
    cesdk.ui.registerComponent('ly.img.crop.dock', ({ builder: { Button } }) => {
      const open = cesdk.ui.isPanelOpen(TRANSFORM_PANEL);

      Button('crop', {
        label: 'Crop',
        icon: '@imgly/Crop',
        isSelected: open,
        onClick: () => (open ? cesdk.ui.closePanel(TRANSFORM_PANEL) : cesdk.ui.openPanel(TRANSFORM_PANEL)),
      });
    });
  };

  // Finds the main video clip (graphic block with a video fill) — used for auto-select
  const findVideoBlock = (engine: CreativeEditorSDK['engine']) => {
    return engine.block.findByType('//ly.img.ubq/graphic').find((id) => {
      if (!engine.block.supportsFill(id)) return false;

      const fill = engine.block.getFill(id);

      return engine.block.getType(fill) === '//ly.img.ubq/fill/video';
    });
  };

  const applySourceSet = async (sourceSetsArg: SourceSetsType, cesdk: CreativeEditorSDK) => {
    const videoFill = cesdk.engine.block.findByType('//ly.img.ubq/fill/video')[0];

    if (videoFill == null) return;

    const meta = mediaRef.current.media?.[0]?.metadata;

    const entries = [
      { uri: originalUrl, width: Number(meta?.width), height: Number(meta?.height) },
      ...sourceSetsArg.map((s) => ({ uri: s.url, width: Number(s.width), height: Number(s.height) })),
    ].filter((e) => Number.isFinite(e.width) && Number.isFinite(e.height));

    if (entries.length === 0) return;

    await cesdk.engine.block.setSourceSet(videoFill, 'fill/video/sourceSet', entries);
  };

  const init = useCallback(
    async (cesdk: CreativeEditorSDK) => {
      // Capture the video's native frame so "Reset" and the inputs have a baseline
      let originalSceneSize: { width: number; height: number } | null = null;
      const initialPage = cesdk.engine.block.findByType('page')[0];

      if (initialPage != null) {
        originalSceneSize = {
          width: cesdk.engine.block.getWidth(initialPage),
          height: cesdk.engine.block.getHeight(initialPage),
        };
      }

      await cesdk.createFromVideo(originalUrl);

      // ========================================================================
      // Configuration Plugin
      // ========================================================================

      await cesdk.addPlugin(new VideoEditorConfig());

      // ========================================================================
      // Asset Source Plugins
      // ========================================================================

      await cesdk.addPlugin(new BlurAssetSource());
      await cesdk.addPlugin(new CaptionPresetsAssetSource());
      await cesdk.addPlugin(new ImageColorsAssetSource());
      await cesdk.addPlugin(new ColorPaletteAssetSource());
      await cesdk.addPlugin(new CropPresetsAssetSource());
      await cesdk.addPlugin(
        new UploadAssetSources({
          include: ['ly.img.image.upload', 'ly.img.video.upload', 'ly.img.audio.upload'],
        })
      );
      await cesdk.addPlugin(new EffectsAssetSource());
      await cesdk.addPlugin(new FiltersAssetSource());
      await cesdk.addPlugin(new StickerAssetSource());
      await cesdk.addPlugin(new TextAssetSource());
      await cesdk.addPlugin(new TextComponentAssetSource());
      await cesdk.addPlugin(new TypefaceAssetSource());
      await cesdk.addPlugin(new VectorShapeAssetSource());

      // ========================================================================
      // Navigation Bar
      // ========================================================================

      cesdk.ui.setComponentOrder({ in: 'ly.img.navigation.bar' }, [
        'ly.img.undoRedo.navigationBar',
        'ly.img.spacer',
        'ly.img.zoom.navigationBar',
        {
          id: 'ly.img.close.navigationBar',
          label: 'Close',
          icon: '@imgly/Cross',
          variant: 'regular',
          onClick: () => {
            window.pendo.track('[CE.SDK] Editor Close', { fileType: 'image' });
            window.parent.postMessage({ event: EDITOR_CLOSE }, '*');
            resetState();
          },
        },
        {
          id: 'ly.img.action.navigationBar',
          key: 'actions.export.custom_label',
          label: 'Save video',
          color: 'accent',
          onClick: async () => {
            saveVideo(cesdk);
          },
        },
      ]);

      // ========================================================================
      // Whole-scene resize (used by Apply, Reset, and each preset tile)
      // Content-aware resize reflows everything on the page, like your panel does.
      // ========================================================================

      setupCropPanel(cesdk, originalSceneSize);

      // Captions: adds a caption track + first caption to the current page
      cesdk.ui.registerComponent('ly.img.captions.dock', ({ builder }) => {
        const { engine } = cesdk;

        builder.Button('captions', {
          label: 'Captions',
          icon: '@imgly/Subtitles', // verify; @imgly/Text is a safe fallback
          onClick: () => {
            const page = engine.scene.getCurrentPage();

            if (page == null) return;

            const track = engine.block.create('captionTrack');

            engine.block.appendChild(page, track);

            const caption = engine.block.create('caption');

            engine.block.setString(caption, 'caption/text', 'New caption');
            engine.block.setDuration(caption, 3);
            engine.block.appendChild(track, caption);
            engine.block.setSelected(caption, true);
          },
        });
      });

      // ========================================================================
      // Dock order: crop, uploads, text, shapes, captions, stickers
      // ========================================================================

      cesdk.ui.setComponentOrder({ in: 'ly.img.dock' }, [
        'ly.img.crop.dock',
        {
          id: 'ly.img.assetLibrary.dock',
          key: 'ly.img.upload',
          icon: '@imgly/Upload',
          label: 'libraries.ly.img.upload.label',
          entries: ['ly.img.upload'],
        },
        {
          id: 'ly.img.assetLibrary.dock',
          key: 'ly.img.text',
          icon: '@imgly/Text',
          label: 'libraries.ly.img.text.label',
          entries: ['ly.img.text'],
        },
        {
          id: 'ly.img.assetLibrary.dock',
          key: 'ly.img.vector.shape',
          icon: '@imgly/Shapes',
          label: 'libraries.ly.img.vector.shape.label',
          entries: ['ly.img.vector.shape'],
        },
        {
          id: 'ly.img.assetLibrary.dock',
          key: 'ly.img.sticker',
          icon: '@imgly/Sticker',
          label: 'libraries.ly.img.sticker.label',
          entries: ['ly.img.sticker'],
        },
      ]);

      // Select the loaded video so clip tools are active immediately
      const videoBlock = findVideoBlock(cesdk.engine);

      if (videoBlock != null) cesdk.engine.block.setSelected(videoBlock, true);

      const sourcesetCheck = setInterval(() => {
        if (mediaRef.current.isSuccess && mediaRef.current.media?.length && mediaRef.current.media[0].sourceSets?.length) {
          applySourceSet(mediaRef.current.media[0].sourceSets, cesdk);
          clearInterval(sourcesetCheck);
        }
      }, 1000);

      // All custom nav/dock/panel overrides are applied by this point —
      // safe to reveal the editor now.
      setIsEditorReady(true);
    },
    [originalUrl]
  );

  return (
    <div className="cesdkWrapperStyle">
      {createPortal(
        <CeSDKWrapper>
          {!isEditorReady && (
            <LoadingWrapper>
              <Spinner size="lg" />
            </LoadingWrapper>
          )}
          <div style={{ visibility: isEditorReady ? 'visible' : 'hidden', width: '100%', height: '100%' }}>
            <MemoCreativeEditor
              config={{ baseURL: '/cesdk', license: 'VHjEPgPLBuDp-hnJmlQe-UAEu1GdyVUXuNAyIuvheFkzoxpkPdl4A1oKbCGgmjsl', theme: 'dark' }}
              init={init}
              width="100vw"
              height="100vh"
            />
          </div>
        </CeSDKWrapper>,
        document.getElementById('modal-root') as HTMLElement
      )}
    </div>
  );
};

export default Editor;
